import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import {
  createMpCuota,
  fmtPct,
  listMpCuotas,
  MP_CUOTAS_QUERY_KEY,
  softDeleteMpCuota,
  updateMpCuota,
  type MpCuota,
} from '@/shared/api/controllers/mpCuotas';
import { ApiError } from '@/shared/api/client';
import type { EntityViewProps } from '../../types';


const schema = z.object({
  cuotas: z
    .string()
    .trim()
    .refine((v) => /^\d+$/.test(v) && Number(v) > 0 && Number(v) <= 48, 'Ingresá un número de cuotas (1 a 48)'),
  porcentaje: z
    .string()
    .trim()
    .refine((v) => {
      const n = Number(v.replace(',', '.'));
      return v !== '' && Number.isFinite(n) && n >= 0 && n < 100;
    }, 'Ingresá un porcentaje entre 0 y 99,99'),
  activo: z.boolean(),
});
type Values = z.infer<typeof schema>;

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: MpCuota }
  | { kind: 'delete'; item: MpCuota };

const mensaje = (err: unknown, fallback: string) =>
  err instanceof ApiError && err.message ? err.message : err instanceof Error ? err.message : fallback;

/**
 * Configuración → Cuotas MercadoPago: % de recargo sobre el total factura
 * según la cantidad de cuotas. Pedido del cliente 2026-10-20.
 */
export function CuotasMercadoPagoEntity({ search, filterValue, onStatus, openCreate, onCreateHandled }: EntityViewProps) {
  const queryClient = useQueryClient();
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: MP_CUOTAS_QUERY_KEY,
    queryFn: listMpCuotas,
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });
  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim();
    return rows
      .filter((r) => !filterValue || r.status === filterValue)
      .filter((r) => !q || String(r.cuotas).includes(q))
      .sort((a, b) => a.cuotas - b.cuotas);
  }, [rows, search, filterValue]);

  const columns: CrudColumn<MpCuota>[] = [
    { header: 'Status', width: 'w-28', render: (r) => <StatusBadge status={r.status} /> },
    {
      header: 'Cuotas',
      render: (r) => <span className="font-medium text-foreground">{r.cuotas} cuotas</span>,
    },
    { header: 'Recargo', render: (r) => <span className="tabular-nums">{fmtPct(r.porcentaje)}</span> },
  ];

  const close = () => setMode({ kind: 'closed' });
  const done = (msg: string) => {
    queryClient.invalidateQueries({ queryKey: MP_CUOTAS_QUERY_KEY });
    close();
    onStatus({ kind: 'success', message: msg });
  };
  const fail = (msg: string) => {
    close();
    onStatus({ kind: 'error', message: msg });
  };

  const del = useMutation({
    mutationFn: (item: MpCuota) => softDeleteMpCuota(item.id),
    onSuccess: () => done('La cuota se dio de baja.'),
    onError: (err) => fail(mensaje(err, 'No se pudo dar de baja la cuota.')),
  });

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin cuotas cargadas.'}
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canDelete={(r) => r.status === 'Activo'}
        deleteDisabledTitle={() => 'El registro ya está inactivo'}
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <CuotaDialog
          item={mode.kind === 'edit' ? mode.item : null}
          existentes={rows}
          onClose={close}
          onSuccess={done}
          onError={fail}
        />
      )}

      <Dialog open={mode.kind === 'delete'} onOpenChange={(o) => !o && !del.isPending && close()}>
        <DialogContent className="max-w-md" processing={del.isPending && 'Dando de baja…'}>
          <DialogHeader>
            <DialogTitle>Dar de baja cuota</DialogTitle>
            <DialogDescription>
              {mode.kind === 'delete' &&
                `${mode.item.cuotas} cuotas (${fmtPct(mode.item.porcentaje)}) deja de ofrecerse en Pagos. Las ventas ya guardadas conservan su recargo.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={del.isPending}>
              Cancelar
            </Button>
            <Button
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={del.isPending}
              onClick={() => mode.kind === 'delete' && del.mutate(mode.item)}
            >
              Dar de baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CuotaDialog({
  item,
  existentes,
  onClose,
  onSuccess,
  onError,
}: {
  item: MpCuota | null;
  existentes: MpCuota[];
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: item
      ? {
          cuotas: String(item.cuotas),
          porcentaje: String(item.porcentaje).replace('.', ','),
          activo: item.status === 'Activo',
        }
      : { cuotas: '', porcentaje: '', activo: true },
  });
  const mutation = useMutation({
    mutationFn: (v: Values) => {
      const payload = {
        cuotas: Number(v.cuotas),
        porcentaje: Number(v.porcentaje.replace(',', '.')),
        status: v.activo ? ('Activo' as const) : ('Inactivo' as const),
      };
      return item ? updateMpCuota(item.id, payload) : createMpCuota(payload);
    },
    onSuccess: () => onSuccess(item ? 'La cuota se actualizó correctamente.' : 'La cuota se creó correctamente.'),
    onError: (err) => {
      // Carrera con otro usuario: el índice único lo frena; avisamos en el campo sin cerrar.
      if (err instanceof ApiError && err.status === 409) return form.setError('cuotas', { message: err.message });
      onError(mensaje(err, 'No se pudo guardar la cuota.'));
    },
  });
  const guardar = (v: Values) => {
    const dup = existentes.find((r) => r.cuotas === Number(v.cuotas) && r.id !== item?.id);
    if (dup) {
      form.setError('cuotas', {
        message:
          dup.status === 'Activo'
            ? `Ya existe ${dup.cuotas} cuotas (${fmtPct(dup.porcentaje)}). Editá esa fila en lugar de crear otra.`
            : `Ya existe ${dup.cuotas} cuotas dada de baja. Editala y activala en lugar de crear otra.`,
      });
      return;
    }
    mutation.mutate(v);
  };
  const err = (k: keyof Values) => form.formState.errors[k]?.message as string | undefined;

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent className="sm:max-w-md" processing={mutation.isPending && 'Guardando…'}>
        <DialogHeader>
          <DialogTitle>{item ? 'Editar cuota' : 'Nueva cuota'}</DialogTitle>
          <DialogDescription>
            El recargo se suma al total con factura cuando el cliente paga con MercadoPago en
            esa cantidad de cuotas.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(guardar)} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="cuotas">
                  Cuotas <span className="text-primary">*</span>
                </Label>
                <Input id="cuotas" variant="filled" inputMode="numeric" placeholder="6" autoFocus {...form.register('cuotas')} />
                {err('cuotas') && <p className="text-xs font-medium text-destructive">{err('cuotas')}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="porcentaje">
                  Recargo % <span className="text-primary">*</span>
                </Label>
                <Input id="porcentaje" variant="filled" inputMode="decimal" placeholder="11,40" {...form.register('porcentaje')} />
                {err('porcentaje') && <p className="text-xs font-medium text-destructive">{err('porcentaje')}</p>}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <Label htmlFor="activo">Activa</Label>
              <Switch
                id="activo"
                checked={form.watch('activo')}
                onCheckedChange={(v) => form.setValue('activo', v)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {item ? 'Guardar cambios' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
